import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import type { ClientContact } from 'src/types/client';
import NextLink from 'next/link';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@savvy-kit/components/dropdown-menu';
import { useIsPageTitleCollapsed } from '@savvy-kit/templates/page-title';
import { isNil } from 'es-toolkit';
import { EllipsisIcon, Presentation } from 'lucide-react';
import { TrackedButton, TrackedDropdownMenuItem, TrackedIconButton } from 'src/components/tracked';

import type { HouseholdAccountFragment } from '../client-page-title-nav-gql';
import type { LogInAsClientButtonFragment } from '../log-in-as-client-button';
import { LogInAsClientMenuItem } from '../log-in-as-client-button';
import { ResetDemoHouseholdMenuItem } from '../reset-demo-household-button';
import { ContactWebLauncher } from './contact-web-launcher';
import { HouseholdCreateMenu } from './household-create-menu';

export type HouseholdHeaderHousehold = ResultOf<typeof HouseholdAccountFragment>;

const OPEN_IN_CANVAS_LABEL = 'Open in Canvas';

type HouseholdHeaderActionsProps = {
  readonly clientFrag: FragmentType<typeof LogInAsClientButtonFragment> | undefined;
  readonly flagEnableUpgradeCustodian: boolean;
  readonly hasActiveEciOnboarding: boolean;
  readonly household: HouseholdHeaderHousehold | null | undefined;
  readonly isArchived: boolean;
  /** Owned by the host so the latching poll is flag-gated and runs once per page — see the
   * `useApolloNetworkQuiet` call in `client-page-title-nav`. */
  readonly pageReady: boolean;
  readonly onUpgradeCustodianClick: () => void;
};

export function HouseholdHeaderActions({
  clientFrag,
  flagEnableUpgradeCustodian,
  hasActiveEciOnboarding,
  household,
  isArchived,
  pageReady,
  onUpgradeCustodianClick,
}: HouseholdHeaderActionsProps) {
  const isCollapsed = useIsPageTitleCollapsed();

  const primaryContact = household?.primaryClient;
  const coClientContact = household?.coClient;
  const isDemo = household?.demo === true;
  const showUpgradeCustodian = !isNil(household) && flagEnableUpgradeCustodian && !hasActiveEciOnboarding;

  const primaryLoginContact: ClientContact | null = isNil(primaryContact)
    ? null
    : {
        Id: primaryContact.id,
        FirstName: primaryContact.user.firstName,
        LastName: primaryContact.user.lastName,
        Email: primaryContact.user.email,
        Savvy_User_Id__c: primaryContact.user.id,
        XLR8CS__Primary_Contact__c: true,
        Co_Client__c: false,
      };

  const coClientLoginContact: ClientContact | null = isNil(coClientContact)
    ? null
    : {
        Id: coClientContact.id,
        FirstName: coClientContact.user.firstName,
        LastName: coClientContact.user.lastName,
        Email: coClientContact.user.email,
        Savvy_User_Id__c: coClientContact.user.id,
        XLR8CS__Primary_Contact__c: false,
        Co_Client__c: true,
      };

  // Every item below is conditional, so an ungated trigger would open an empty popup.
  const hasOverflowItems = !isNil(primaryLoginContact) || showUpgradeCustodian || isDemo;

  return (
    <>
      {/* Keyed: the layout shell survives a household switch, so an open note would otherwise save to the next one. */}
      <HouseholdCreateMenu key={household?.id} householdId={household?.id} isArchived={isArchived} />
      {!isNil(household) &&
        household.salesforceAccountId &&
        (isCollapsed ? (
          <TrackedIconButton
            size="sm"
            variant="secondary"
            elementName="open-in-canvas-button"
            aria-label={OPEN_IN_CANVAS_LABEL}
            render={<NextLink href={`/advisor/canvas/household/${household.salesforceAccountId}`} />}
          >
            <Presentation />
          </TrackedIconButton>
        ) : (
          <TrackedButton
            size="sm"
            variant="secondary"
            elementName="open-in-canvas-button"
            className="py-0"
            icon={<Presentation />}
            render={<NextLink href={`/advisor/canvas/household/${household.salesforceAccountId}`} />}
          >
            {OPEN_IN_CANVAS_LABEL}
          </TrackedButton>
        ))}
      <ContactWebLauncher householdId={household?.id} />
      {hasOverflowItems && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <TrackedIconButton
                size="sm"
                variant="ghost"
                elementName="household-header-more-actions-button"
                aria-label="More actions"
              />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="flex flex-col w-[220px]">
            {!isNil(primaryLoginContact) && (
              <LogInAsClientMenuItem
                clientFrag={clientFrag}
                contact={primaryLoginContact}
                co_contact={coClientLoginContact}
                disabled={isArchived}
                pageReady={pageReady}
              />
            )}
            {showUpgradeCustodian && (
              <TrackedDropdownMenuItem elementName="upgrade-custodian-menu-item" onClick={onUpgradeCustodianClick}>
                Upgrade custodian
              </TrackedDropdownMenuItem>
            )}
            {isDemo && !isNil(household) && <ResetDemoHouseholdMenuItem householdId={household.id} />}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
